import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard, Globe, Server, HardDrive, Key, LogOut,
  Users as UsersIcon, Send, ShieldAlert, Activity, Search, Bell, Shield, Radio
} from 'lucide-react';
import api from './api';
import AntoraSymbol from './components/AntoraSymbol';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Domains from './pages/Domains';
import DomainDetail from './pages/DomainDetail';
import DnsRecursor from './pages/DnsRecursor';
import Radius from './pages/Radius';
import DnsZones from './pages/DnsZones';
import DnsDnssec from './pages/DnsDnssec';
import DnsReverse from './pages/DnsReverse';
import EmailAccounts from './pages/EmailAccounts';
import Webmail from './pages/Webmail';
import Services from './pages/Services';
import Backups from './pages/Backups';
import Credentials from './pages/Credentials';
import Rspamd from './pages/Rspamd';
import Monitoring from './pages/Monitoring';
import Users from './pages/Users';
import MailRelay from './pages/MailRelay';
import RPKI from './pages/RPKI';
import AsnResources from './pages/AsnResources';
import RpkiCA from './pages/RpkiCA';

function Sidebar({ me, onLogout }) {
  const loc = useLocation();
  const nav = useNavigate();
  const isAdmin = me?.role === 'admin' || me?.canAll;
  const isActive = p => loc.pathname.startsWith(p);

  const principal = [
    { path: '/dashboard',   label: 'Dashboard',      icon: LayoutDashboard },
    { path: '/domains',     label: 'Domínios',       icon: Globe },
    { path: '/dns/recursor',label: 'Resolvedor DNS', icon: Shield },
    { path: '/radius',      label: 'FreeRADIUS',     icon: Radio },
  ];
  const sistema = [
    { path: '/users',      label: 'Usuários',        icon: UsersIcon },
    { path: '/services',   label: 'Serviços Docker', icon: Server },
    { path: '/dns/monitor',label: 'Monitor DNS',     icon: Activity },
    { path: '/email/relay',label: 'SMTP Relay',      icon: Send },
    { path: '/rspamd',     label: 'Rspamd',          icon: ShieldAlert },
    { path: '/backups',    label: 'Backups',         icon: HardDrive },
    { path: '/credentials',label: 'Credenciais',     icon: Key },
  ];

  return (
    <aside className="sidebar">
      <div className="brand">
        <AntoraSymbol size={30}/>
        <div>
          <div className="brand-name">antora<span style={{ color: 'var(--accent)' }}>.</span></div>
          <div className="brand-sub">Services</div>
        </div>
      </div>

      <div className="sidebar-group">Principal</div>
      {principal.map(it => (
        <button key={it.path}
          className={'sidebar-item ' + (isActive(it.path) ? 'active' : '')}
          onClick={() => nav(it.path)}>
          <it.icon size={16} className="icon"/> {it.label}
        </button>
      ))}

      {isAdmin && (
        <>
          <div className="sidebar-group">Sistema</div>
          {sistema.map(it => (
            <button key={it.path}
              className={'sidebar-item ' + (isActive(it.path) ? 'active' : '')}
              onClick={() => nav(it.path)}>
              <it.icon size={16} className="icon"/> {it.label}
            </button>
          ))}
        </>
      )}

      <div className="sidebar-user">
        <div className="avatar">{(me?.username || 'A')[0].toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.2 }}>{me?.username || 'Operador'}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-mute)', letterSpacing: '0.05em' }}>
            {isAdmin ? 'ADMIN' : 'USER'}
          </div>
        </div>
        <button className="btn btn-icon btn-ghost" title="Sair" onClick={onLogout}>
          <LogOut size={14}/>
        </button>
      </div>
    </aside>
  );
}

function Topbar() {
  const loc = useLocation();
  const params = useParams();

  // Breadcrumbs a partir do path
  const crumbs = (() => {
    const path = loc.pathname.split('/').filter(Boolean);
    if (path[0] === 'dashboard') return ['Dashboard'];
    if (path[0] === 'domains' && path[1]) return ['Domínios', path[1]];
    if (path[0] === 'domains') return ['Domínios'];
    const labelMap = {
      users:'Usuários', services:'Serviços Docker', backups:'Backups', credentials:'Credenciais',
      rspamd:'Rspamd', dns:'DNS', email:'E-mail', monitor:'Monitor', relay:'SMTP Relay',
      zones:'Zonas', dnssec:'DNSSEC', reverse:'Reverso', accounts:'Contas', webmail:'Webmail',
      rpki:'RPKI', resources:'Recursos ASN', ca:'CA',
    };
    return path.map(p => labelMap[p] || p);
  })();

  return (
    <header className="topbar">
      <div className="breadcrumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">/</span>}
            <span className={'crumb ' + (i === crumbs.length - 1 ? 'current' : '')}>{c}</span>
          </React.Fragment>
        ))}
      </div>
      <div className="spacer"/>
      <button className="btn btn-icon btn-ghost" title="Buscar"><Search size={16}/></button>
      <button className="btn btn-icon btn-ghost" title="Notificações" style={{ position: 'relative' }}>
        <Bell size={16}/>
        <span style={{ position: 'absolute', top: 4, right: 4, width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)' }}/>
      </button>
    </header>
  );
}

function Shell({ children }) {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  useEffect(() => { api.get('/me').then(r => setMe(r.data.user)).catch(()=>{}); }, []);
  const logout = () => { localStorage.removeItem('tguth_token'); nav('/login'); };

  return (
    <div className="app">
      <Sidebar me={me} onLogout={logout}/>
      <div className="main">
        <Topbar/>
        <div className="content">{children}</div>
      </div>
    </div>
  );
}

const Protected = ({ children }) => {
  const t = localStorage.getItem('tguth_token');
  if (!t) return <Navigate to="/login" replace />;
  return <Shell>{children}</Shell>;
};

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard"     element={<Protected><Dashboard /></Protected>} />
        <Route path="/domains"       element={<Protected><Domains /></Protected>} />
        <Route path="/domains/:name" element={<Protected><DomainDetail /></Protected>} />
        <Route path="/dns/recursor"  element={<Protected><DnsRecursor /></Protected>} />
        <Route path="/radius"        element={<Protected><Radius /></Protected>} />

        <Route path="/users"         element={<Protected><Users /></Protected>} />
        <Route path="/services"      element={<Protected><Services /></Protected>} />
        <Route path="/dns/monitor"   element={<Protected><Monitoring /></Protected>} />
        <Route path="/email/relay"   element={<Protected><MailRelay /></Protected>} />
        <Route path="/rspamd"        element={<Protected><Rspamd /></Protected>} />
        <Route path="/backups"       element={<Protected><Backups /></Protected>} />
        <Route path="/credentials"   element={<Protected><Credentials /></Protected>} />

        {/* Rotas antigas (back-compat, sem link na sidebar) */}
        <Route path="/dns/zones"     element={<Protected><DnsZones /></Protected>} />
        <Route path="/dns/dnssec"    element={<Protected><DnsDnssec /></Protected>} />
        <Route path="/dns/reverse"   element={<Protected><DnsReverse /></Protected>} />
        <Route path="/email/accounts"element={<Protected><EmailAccounts /></Protected>} />
        <Route path="/email/webmail" element={<Protected><Webmail /></Protected>} />
        <Route path="/rpki"          element={<Protected><RPKI /></Protected>} />
        <Route path="/rpki/resources" element={<Protected><AsnResources /></Protected>} />
        <Route path="/rpki/ca"       element={<Protected><RpkiCA /></Protected>} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
